import { submitFormToServer } from "../api/submitForm";
import { isOnline, onConnectivityChange } from "./connectivity";
import {
  getQueuedSubmissions,
  getSyncingSubmissions,
  updateSubmissionStatus,
  clearSyncedSubmissions,
} from "./queue";
import type { QueuedSubmission } from "./types";

const MAX_RETRIES = 10;
const BASE_DELAY_MS = 2_000;

type SyncEventType =
  | "sync:start"
  | "sync:complete"
  | "sync:error"
  | "sync:item"
  | "persistence:granted"
  | "persistence:denied";
type SyncEventListener = (event: SyncEventType, data?: unknown) => void;

let syncInProgress = false;
let syncTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Watchdog against a stranded `syncInProgress` flag. Every trigger funnels
 * through `runSyncLoop`, so a flag stuck `true` silently disables ALL future
 * drains — the exact "badge says Syncing… forever" failure. Two known ways to
 * strand it: a fetch that never settles (now also bounded by request
 * timeouts), and a bfcache restore resurrecting the heap of a page whose
 * running loop died with it (also handled by the pageshow reset in
 * startSyncService).
 *
 * The heartbeat is stamped at loop start, per processed item, and after each
 * photo-upload attempt settles (attachments.ts) — the longest legitimate gap
 * between beats is one bounded request (~120s). If the flag is set but the
 * heartbeat is older than SYNC_STALL_MS, the old loop is presumed dead and a
 * new one may start. Worst case on a false positive: recoverStuckSyncing
 * requeues a row the old loop was still sending, and the server-side
 * idempotencyKey dedups the double-send.
 */
const SYNC_STALL_MS = 5 * 60_000;
let syncHeartbeatAt = 0;

export function beatSyncHeartbeat(): void {
  syncHeartbeatAt = Date.now();
}
let unsubConnectivity: (() => void) | null = null;
let unsubReconcile: (() => void) | null = null;
let eventListeners: SyncEventListener[] = [];

/**
 * Whether this origin's IndexedDB is in the durable ("persistent") bucket.
 *   true  → granted; queued records are safe from eviction.
 *   false → DENIED by the browser; queued/failed records are eviction-eligible
 *           and can be lost permanently before they sync. This is the one state
 *           a no-loss system must treat as an alarm, not a shrug.
 *   null  → not yet determined / unsupported environment.
 */
let persistenceGranted: boolean | null = null;

/** Read the current storage-durability state (see `persistenceGranted`). */
export function getPersistenceState(): boolean | null {
  return persistenceGranted;
}

function emit(event: SyncEventType, data?: unknown) {
  for (const fn of eventListeners) {
    try {
      fn(event, data);
    } catch {
      // don't let listener errors break the loop
    }
  }
}

export function onSyncEvent(fn: SyncEventListener): () => void {
  eventListeners.push(fn);
  return () => {
    eventListeners = eventListeners.filter((l) => l !== fn);
  };
}

/**
 * Move this origin's IndexedDB into the durable ("persistent") storage bucket
 * so queued submissions and staged photo blobs can't be silently evicted under
 * storage pressure or WebKit's ~7-day no-interaction purge. Anything still in
 * `queued`/`failed` lives ONLY on the device until it syncs, so eviction there
 * is unrecoverable data loss — we request persistence the instant the offline
 * service starts, well before the first enqueue.
 *
 * Critically, the browser may DENY the grant based on its own engagement
 * heuristics. We capture that result rather than fire-and-forget: a denied
 * grant is the precise eviction-eligible state a no-loss system must surface
 * (via the `persistence:denied` event + `getPersistenceState()`), not assume
 * away. Idempotent and best-effort; never throws.
 */
async function requestPersistentStorage(): Promise<void> {
  try {
    if (typeof navigator === "undefined" || !navigator.storage?.persist) {
      persistenceGranted = null;
      return;
    }
    if (await navigator.storage.persisted()) {
      persistenceGranted = true;
      emit("persistence:granted");
      return;
    }
    const granted = await navigator.storage.persist();
    persistenceGranted = granted;
    if (granted) {
      emit("persistence:granted");
    } else {
      // Loud on purpose: queued records are now eviction-eligible until synced.
      console.warn(
        "[offline-sync] Persistent storage NOT granted — unsynced submissions are eviction-eligible and may be lost. Drain the queue before backgrounding the app.",
      );
      emit("persistence:denied");
    }
  } catch {
    // Probing storage must never break startup; leave state undetermined.
    persistenceGranted = null;
  }
}

async function recoverStuckSyncing(): Promise<void> {
  const stuck = await getSyncingSubmissions();
  for (const sub of stuck) {
    await updateSubmissionStatus(sub.localSubmissionId, "queued");
  }
}

async function processOne(submission: QueuedSubmission): Promise<void> {
  await updateSubmissionStatus(submission.localSubmissionId, "syncing");
  emit("sync:item", { id: submission.localSubmissionId, status: "syncing" });

  /**
   * Step 1: walk the payload and upload any locally-staged attachments
   * (`local://attach_<id>` placeholders), swapping them for R2 keys before
   * we send the submission. This makes photos work fully offline — the tech
   * captures, the blob lives in IndexedDB until the network returns, then
   * everything drains in one sync cycle.
   *
   * Resolution is wrapped in a try/catch so a single failed photo just
   * marks the submission retryable rather than crashing the whole sync.
   */
  let resolvedPayload: Record<string, unknown>
  try {
    const { resolveLocalAttachmentRefs } = await import("./attachments")
    resolvedPayload = await resolveLocalAttachmentRefs({
      formUrl: submission.formUrl,
      payload: submission.payloadJson,
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    const nextRetry = submission.retryCount + 1
    const tooMany = nextRetry >= MAX_RETRIES
    await updateSubmissionStatus(
      submission.localSubmissionId,
      tooMany ? "failed" : "queued",
      { lastError: `Photo upload failed: ${message}`, retryCount: nextRetry }
    )
    emit("sync:item", {
      id: submission.localSubmissionId,
      status: tooMany ? "failed" : "queued",
      error: message,
      retryCount: nextRetry,
    })
    return
  }

  const result = await submitFormToServer({
    localSubmissionId: submission.localSubmissionId,
    formUrl: submission.formUrl,
    formVersion: submission.formVersion,
    jobId: submission.jobId ?? null,
    sourceDraftId: submission.sourceDraftId ?? null,
    idempotencyKey: submission.idempotencyKey,
    submittedAtClient: submission.createdAt,
    answers: resolvedPayload,
  });

  if (result.ok) {
    await updateSubmissionStatus(submission.localSubmissionId, "synced", {
      syncedAt: new Date().toISOString(),
    });
    emit("sync:item", {
      id: submission.localSubmissionId,
      status: "synced",
      serverSubmissionId: result.serverSubmissionId,
    });
    // Best-effort cleanup of uploaded photo blobs so IndexedDB doesn't grow
    // unbounded over time. Failures here are non-fatal.
    try {
      const { cleanupUploadedAttachments } = await import("./attachments")
      await cleanupUploadedAttachments({
        formUrl: submission.formUrl,
        // Scoped to this submission's job: the same form is filled for many
        // jobs on one device, and a form-wide delete would take the staged
        // blobs of another job's half-finished draft with it.
        jobId: submission.jobId ?? null,
      })
    } catch {
      // ignore — quota cleanup is opportunistic
    }
    return;
  }

  const nextRetry = submission.retryCount + 1;

  if (!result.retryable || nextRetry >= MAX_RETRIES) {
    await updateSubmissionStatus(submission.localSubmissionId, "failed", {
      lastError: result.error,
      retryCount: nextRetry,
    });
    emit("sync:item", {
      id: submission.localSubmissionId,
      status: "failed",
      error: result.error,
    });
    return;
  }

  await updateSubmissionStatus(submission.localSubmissionId, "queued", {
    lastError: result.error,
    retryCount: nextRetry,
  });
  emit("sync:item", {
    id: submission.localSubmissionId,
    status: "queued",
    error: result.error,
    retryCount: nextRetry,
  });
}

export async function runSyncLoop(): Promise<void> {
  if (syncInProgress) {
    if (Date.now() - syncHeartbeatAt < SYNC_STALL_MS) return;
    console.warn(
      "[offline-sync] sync loop stalled (no heartbeat for 5m) — presuming it dead and starting a fresh drain",
    );
  }
  if (!isOnline()) return;

  syncInProgress = true;
  beatSyncHeartbeat();
  emit("sync:start");

  try {
    await recoverStuckSyncing();

    const queued = await getQueuedSubmissions();
    for (const submission of queued) {
      if (!isOnline()) break;
      beatSyncHeartbeat();
      await processOne(submission);
    }

    // Server-confirmed reconcile: heal any record the 200 OK lied about and
    // stamp `serverConfirmedAt` so cleanup can safely drop confirmed copies.
    // Must run before clearSyncedSubmissions, which now gates on that stamp.
    if (isOnline()) {
      try {
        const { runReconcile } = await import("./reconcile");
        const { requeued } = await runReconcile();
        // Reconcile may have re-queued a `synced` row the server never got;
        // schedule another pass to drain it (can't recurse — we're inside the
        // loop and syncInProgress is still true).
        if (requeued > 0) scheduleSyncRetry();
      } catch {
        // reconcile is best-effort; never let it break the drain loop.
      }
    }

    clearSyncedSubmissions().catch(() => {});
  } catch (err) {
    emit("sync:error", err);
  } finally {
    syncInProgress = false;
    emit("sync:complete");
  }
}

export function scheduleSyncRetry(delayMs?: number): void {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    runSyncLoop();
  }, delayMs ?? BASE_DELAY_MS);
}

export async function enqueueAndSync(params: {
  formUrl: string;
  formVersion: string;
  jobId?: string | null;
  sourceDraftId?: string | null;
  payload: Record<string, unknown>;
}): Promise<QueuedSubmission> {
  const { enqueueSubmission } = await import("./queue");
  const submission = await enqueueSubmission(params);

  if (isOnline()) {
    runSyncLoop();
  }

  return submission;
}

let serviceRefCount = 0;
let serviceTeardown: (() => void) | null = null;

/**
 * Ref-counted so the provider can be mounted at the app root AND in nested
 * layouts (/submit, /offline) without double-registering listeners or having an
 * inner layout's unmount tear the service down from under the outer one. Only
 * the first mount starts the service; only the last unmount stops it.
 */
export function startSyncService(): () => void {
  serviceRefCount++;
  if (serviceRefCount === 1) {
    serviceTeardown = startSyncServiceInner();
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    serviceRefCount--;
    if (serviceRefCount === 0 && serviceTeardown) {
      serviceTeardown();
      serviceTeardown = null;
    }
  };
}

function startSyncServiceInner(): () => void {
  // Lock storage into the durable bucket BEFORE anything can be enqueued, so
  // the public/anonymous submit path is protected against IndexedDB eviction
  // exactly like the authenticated app shell already is.
  void requestPersistentStorage();

  // On native, recover any records the OS-sandbox mirror holds that IndexedDB
  // is missing (i.e. IndexedDB was evicted) BEFORE the first drain, so restored
  // rows sync immediately. No-op + instant on web.
  void (async () => {
    try {
      const { rehydrateFromMirror, rehydrateDraftsFromMirror } = await import(
        "./native-mirror"
      );
      // Attachments/submissions first, then drafts — a rehydrated draft's
      // `local://attach_` refs (incl. the aerial snapshot) resolve to blobs the
      // attachment restore has already put back.
      await rehydrateFromMirror();
      await rehydrateDraftsFromMirror();
    } catch {
      // recovery is best-effort; IndexedDB remains the live store.
    }
    // Native: own photo-attachment background-upload results in the shared
    // reconciler, re-run the loop after each reconcile (a just-learned R2 key
    // lets a queued submission send), and reconcile once now for uploads that
    // finished while the app was killed. No-op on web; foreground upload at
    // submit is the fallback if any of this is unavailable.
    try {
      const [{ applyBackgroundAttachmentResult }, reconcile] = await Promise.all([
        import("./attachments"),
        import("./background-upload-reconcile"),
      ]);
      reconcile.registerUploadHandler("attachment", applyBackgroundAttachmentResult);
      unsubReconcile = reconcile.onAfterReconcile(() => runSyncLoop());
      await reconcile.reconcileBackgroundUploads();
    } catch {
      // optimization only — submit-time upload still covers every photo.
    }
    // Disk hygiene: drop multipart envelopes nothing will ever collect (app
    // killed between build and enqueue, or a build with no BackgroundUploader
    // at all). Starts after the reconcile above — anything that just completed
    // has already had its source removed by the native side, so it won't be
    // miscounted as an orphan — but deliberately NOT awaited: this is disk
    // cleanup and must never sit between app start and the first sync pass.
    void (async () => {
      try {
        const { sweepOrphanedUploadEnvelopes } = await import("./background-uploader");
        await sweepOrphanedUploadEnvelopes();
      } catch {
        // best-effort.
      }
    })();
    runSyncLoop();
  })();

  unsubConnectivity = onConnectivityChange((online) => {
    if (online) {
      runSyncLoop();
    }
  });

  // Periodic sweep every 30 seconds for anything that got stuck
  const interval = setInterval(() => {
    if (isOnline()) runSyncLoop();
  }, 30_000);

  // Lifecycle-driven instant drain. This is the primary coverage for the iPad
  // Capacitor app: WKWebView has no Background Sync, but it DOES fire
  // visibilitychange/pagehide/focus as the app is foregrounded, resumed, or
  // about to be backgrounded — so we drain the queue the moment the tech
  // returns to the app or network conditions likely changed, rather than
  // waiting up to 30s for the periodic sweep.
  const onVisible = () => {
    if (document.visibilityState === "visible" && isOnline()) runSyncLoop();
  };
  const onPageHideOrFocus = () => {
    if (isOnline()) runSyncLoop();
  };
  // bfcache restore resurrects the whole JS heap — including a syncInProgress
  // flag whose owning loop died with the navigated-away page. Every drain
  // trigger would then no-op at the guard forever. Force-release the flag and
  // drain immediately; recoverStuckSyncing requeues the row the dead loop left
  // in "syncing".
  const onPageShow = (e: PageTransitionEvent) => {
    if (!e.persisted) return;
    syncInProgress = false;
    if (isOnline()) runSyncLoop();
  };
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisible);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("pagehide", onPageHideOrFocus);
    window.addEventListener("focus", onPageHideOrFocus);
    window.addEventListener("pageshow", onPageShow);
  }

  return () => {
    if (unsubConnectivity) {
      unsubConnectivity();
      unsubConnectivity = null;
    }
    if (unsubReconcile) {
      unsubReconcile();
      unsubReconcile = null;
    }
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
    clearInterval(interval);
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisible);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", onPageHideOrFocus);
      window.removeEventListener("focus", onPageHideOrFocus);
      window.removeEventListener("pageshow", onPageShow);
    }
  };
}

export function retryFailed(localSubmissionId: string): Promise<void> {
  // Reset retryCount so the manual retry gets a full fresh cycle. Without this
  // the row is still at >= MAX_RETRIES, so processOne would re-fail it on the
  // first transient error instead of genuinely re-driving it.
  return updateSubmissionStatus(localSubmissionId, "queued", {
    lastError: null,
    retryCount: 0,
  }).then(() => {
    runSyncLoop();
  });
}

export { syncInProgress };
